import React from 'react';

import { useEvent } from '~features/events/hooks/useEvent';
import { useSelector } from '../../hooks/usePrerenderUser';
import { useRenderProofExperience } from './useRenderProofExperience';

/**
 * Records the experiment arm on the Mixpanel profile.
 *
 * Events carry `render_proof_experience` only where the feature explicitly
 * attaches it, and there are no super properties, so an account's arm is
 * otherwise knowable only from `Integration: Verification Completed` — a single
 * event fired once, at integration. That is not enough to read the experiment:
 *
 * - the control arm emits no render-proof events at all, so it exists in the
 *   data only through that one anchor;
 * - the dashboard entry point serves accounts that integrated up to two weeks
 *   ago, including before the flag was seeded, whose anchor event either
 *   predates the property or recorded the flag as off.
 *
 * As a profile property the arm segments *every* event, including ones that
 * predate this feature.
 *
 * Deliberately written for everyone — both arms. Restricting it to the arm that
 * sees the proof would leave exactly the comparison group unlabelled.
 */
export function useRenderProofExperimentProfile(): void {
  const experience = useRenderProofExperience();
  const accountId = useSelector((state) => state.prerenderUser.companyId);
  const { updateUserProfile } = useEvent();

  // `useEvent` returns a new object each render; the effect must not depend on it.
  const updateUserProfileRef = React.useRef(updateUserProfile);
  updateUserProfileRef.current = updateUserProfile;

  // Per mount, not per module. A module-level record would dedupe across the
  // whole page load, but shared mutable state that outlives every component is
  // exactly what makes tests have to work around each other — and a repeat
  // write costs nothing, since profile writes are idempotent.
  const writtenRef = React.useRef<string>();

  React.useEffect(() => {
    if (!accountId) return;

    const key = `${accountId}:${experience}`;
    if (writtenRef.current === key) return;
    writtenRef.current = key;

    updateUserProfileRef.current({
      render_proof_experience: experience,
    });
  }, [accountId, experience]);
}
